/* ==========================================================================
   La Banda del Riccio — Menu mobile a pannelli laterali

   Custom element <bdr-mobile-menu>: apre il drawer da sinistra, gestisce
   la pila dei pannelli di secondo/terzo livello, il blocco dello scroll,
   la trappola del focus, Escape e il ritorno del focus all'hamburger.
   Solo classi CSS: le transizioni vivono in bdr-mobile-menu.css.
   ========================================================================== */

(function () {
  'use strict';

  var TRANSITION_FALLBACK_MS = 450;

  /* Esegue il callback a fine transizione dell'elemento, con riserva a tempo
     nel caso la transizione non parta (es. prefers-reduced-motion). */
  function afterTransition(el, callback) {
    var done = false;
    function run() {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      callback();
    }
    function onEnd(event) {
      if (event.target === el) run();
    }
    el.addEventListener('transitionend', onEnd);
    window.setTimeout(run, TRANSITION_FALLBACK_MS);
  }

  /* Un elemento è raggiungibile dal focus solo se visibile e fuori da
     contesti inert (pannelli rimasti dietro). */
  function isReachable(el) {
    if (el.closest('[inert]')) return false;
    if (!el.getClientRects().length) return false;
    return window.getComputedStyle(el).visibility !== 'hidden';
  }

  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class BdrMobileMenu extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.toggle = this.querySelector('[data-bdr-toggle]');
      this.layer = this.querySelector('[data-bdr-layer]');
      if (!this.toggle || !this.layer) return;

      /* Il livello del drawer viene spostato su <body>: lo sticky header
         può ricevere transform e romperebbe il position: fixed.
         Prima si rimuovono eventuali livelli orfani lasciati da un
         re-render della sezione (editor tema). */
      document.body.querySelectorAll(':scope > .bdr-mmenu__layer').forEach(function (stale) {
        stale.remove();
      });
      document.body.appendChild(this.layer);
      document.documentElement.classList.remove('bdr-mmenu-lock');

      this.drawer = this.layer.querySelector('[data-bdr-drawer]');
      this.scrim = this.layer.querySelector('[data-bdr-scrim]');
      this.closeButton = this.layer.querySelector('[data-bdr-close]');
      this.rootPanel = this.layer.querySelector('[data-bdr-panel-root]');
      this.stack = [];

      var self = this;

      this.toggle.addEventListener('click', function () {
        self.isOpen() ? self.close() : self.open();
      });
      this.scrim.addEventListener('click', function () {
        self.close();
      });
      this.closeButton.addEventListener('click', function () {
        self.close();
      });

      this.layer.querySelectorAll('[data-bdr-open]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.openPanel(button);
        });
      });
      this.layer.querySelectorAll('[data-bdr-back]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.closePanel();
        });
      });

      this.layer.addEventListener('keydown', this.onKeydown.bind(this));

      /* Sopra i 990px il drawer non esiste: se aperto, si chiude da solo. */
      var desktopQuery = window.matchMedia('(min-width: 990px)');
      var onBreakpointChange = function (event) {
        if (event.matches && self.isOpen()) self.close({ restoreFocus: false });
      };
      if (desktopQuery.addEventListener) {
        desktopQuery.addEventListener('change', onBreakpointChange);
      } else if (desktopQuery.addListener) {
        desktopQuery.addListener(onBreakpointChange);
      }
    }

    isOpen() {
      return this.layer.classList.contains('is-open');
    }

    open() {
      var self = this;
      this.layer.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('bdr-mmenu-lock');
      /* Focus sul primo elemento del drawer (il bottone di chiusura). */
      afterTransition(this.drawer, function () {
        if (self.isOpen()) self.closeButton.focus();
      });
    }

    close(options) {
      var restoreFocus = !options || options.restoreFocus !== false;

      this.layer.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('bdr-mmenu-lock');

      /* Riporta tutti i pannelli allo stato di partenza. */
      this.stack.forEach(function (entry) {
        entry.opener.setAttribute('aria-expanded', 'false');
      });
      this.stack = [];
      this.layer.querySelectorAll('[data-bdr-panel]').forEach(function (panel) {
        panel.classList.remove('is-active', 'is-behind');
        panel.removeAttribute('inert');
      });
      this.rootPanel.classList.remove('is-behind');
      this.rootPanel.removeAttribute('inert');

      if (restoreFocus) this.toggle.focus();
    }

    currentContext() {
      return this.stack.length ? this.stack[this.stack.length - 1].panel : this.rootPanel;
    }

    openPanel(opener) {
      var targetId = opener.getAttribute('aria-controls');
      var panel = this.layer.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(targetId) : targetId));
      if (!panel) return;

      var previous = this.currentContext();
      previous.classList.add('is-behind');
      previous.setAttribute('inert', '');

      panel.classList.add('is-active');
      opener.setAttribute('aria-expanded', 'true');
      this.stack.push({ panel: panel, opener: opener });

      var self = this;
      var backButton = panel.querySelector('[data-bdr-back]');
      afterTransition(panel, function () {
        var top = self.stack[self.stack.length - 1];
        if (top && top.panel === panel && backButton) backButton.focus();
      });
    }

    closePanel() {
      var entry = this.stack.pop();
      if (!entry) return;

      entry.panel.classList.remove('is-active');
      entry.opener.setAttribute('aria-expanded', 'false');

      var previous = this.currentContext();
      previous.classList.remove('is-behind');
      previous.removeAttribute('inert');

      /* Il pannello precedente è già in vista: il focus torna subito
         alla voce che aveva aperto il pannello chiuso. */
      entry.opener.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.isOpen()) return;

      /* Trappola del focus dentro al drawer, sui soli elementi visibili. */
      var focusables = Array.prototype.filter.call(
        this.drawer.querySelectorAll(FOCUSABLE_SELECTOR),
        isReachable
      );
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('bdr-mobile-menu')) {
    customElements.define('bdr-mobile-menu', BdrMobileMenu);
  }
})();
